<script setup lang="ts">
import IconChevronBottom from "@/assets/icons/IconChevronBottom.vue";
import {MAP_DISPLAY_PARAMS_LABELS, type MapDisplayParamsKey} from "@/composables/constants/params";
import {ref, defineModel} from "vue";
import IconStars from "@/assets/icons/IconStars.vue";
import {useStore} from "@/stores";
import ForecastButton from "@/components/ui/buttons/ForecastButton.vue";
import MonthYearPicker from "@/components/ui/input/MonthYearPicker.vue";
import {useFilesStore} from "@/stores/files";
import {storeToRefs} from "pinia";
import RegionSelector from "@/components/ui/input/RegionSelector.vue";
import type {DistrictKey} from "@/composables/functions/getDistrictName";
import type {RegionKey} from "@/composables/functions/getRegionName";

const selectedParams = defineModel<MapDisplayParamsKey>("selectedParams", {required: true});
const selectedMonth = defineModel<string>("selectedMonth", {required: true});
const selectedYear = defineModel<number>("selectedYear", {required: true});
const selectedDistrict = defineModel<DistrictKey | null>("selectedDistrict", {required: true});
const selectedRegion = defineModel<RegionKey | null>("selectedRegion", {required: true});

const mainStore = useStore();
const filesStore = useFilesStore();

const { filesData } = storeToRefs(filesStore);

// Отображение модального окна параметров
const isShowParamsSelect = ref(false);
const toggleParamsSelect = (): void => {
  isShowParamsSelect.value = !isShowParamsSelect.value;
};
</script>

<template>
  <div class="map-toolbar">
    <!-- Выбор параметров -->
    <SimpleDropdown
      v-tooltip:0="MAP_DISPLAY_PARAMS_LABELS[selectedParams]"
      :use-cross-icon="false"
      teleport="#app"
      :is-visible="isShowParamsSelect"
      max-height="400px"
      max-width="290px"
      :use-searchbar="false"
      :offset="0"
      @close="toggleParamsSelect"
    >
      <template #trigger>
        <div
          class="selector"
          @click="toggleParamsSelect"
        >
          <span class="selector__label">
            {{ MAP_DISPLAY_PARAMS_LABELS[selectedParams] }}
          </span>
          <span
            class="selector__icon"
            :class="{'selector__icon--active': isShowParamsSelect}"
          >
            <IconChevronBottom />
          </span>
        </div>
      </template>
      <template #dropdown-item>
        <ListView>
          <ListItem
            v-for="(value, key) of MAP_DISPLAY_PARAMS_LABELS"
            :key="key"
            v-model="selectedParams"
            :item="key"
            :use-checkbox="false"
          >
            {{ value }}
          </ListItem>
        </ListView>
      </template>
    </SimpleDropdown>
    <!--  Календарь  -->
    <MonthYearPicker
      v-model:model-month="selectedMonth"
      v-model:model-year="selectedYear"
    />
    <!--  Выбор округа/региона  -->
    <RegionSelector
      v-model:selected-district="selectedDistrict"
      v-model:selected-region="selectedRegion"
    />
    <!--  Кнопка прогноза  -->
    <ForecastButton
      class="map-toolbar__forecast"
      :disabled="!mainStore.forecastStatus || filesData?.results.length === 0"
      size="large"
      :active="mainStore.forecastStatusDisplay"
      @click="mainStore.toggleForecastStatusDisplay()"
    >
      <IconStars />
      Прогноз 2025
    </ForecastButton>
  </div>
</template>

<style scoped lang="scss">
.map-toolbar {
  display: flex;
  gap: 10px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;

  &__forecast {
    margin-left: auto !important;
  }
}

.selector {
  background-color: var(--background-bg-white);
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 10px;
  border-radius: 8px;
  min-width: 290px;
  max-width: 290px;
  height: 36px;

  &__label {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    color: var(--text-text-main);
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
  }

  &__text {
    color: var(--text-text-pur);
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
  }

  &__icon {
    color: var(--icon-icon-secondary);
    margin-left: auto;
    cursor: pointer;
    user-select: none;
    transition: transform .2s;

    &--active {
      color: var(--icon-icon-on);
      transform: rotate(180deg);
    }
  }
}

</style>